import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, ChevronRight } from 'lucide-react'
import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { useEntries, useEntryStats } from '@/hooks/useData'
import EntryCard from '@/components/timeline/EntryCard'
import OnThisDayBanner, { YearScroller } from '@/components/timeline/OnThisDayBanner'
import type { Entry } from '@/types/database'

const TAGS = [
  'All',
  '🎂 Birthday',
  '✈️ Travel',
  '🏆 Milestone',
  '📖 Journal',
  '👨‍👩‍👧 Family',
  '⏳ Capsule',
]

export default function TimelinePage() {
  const [selectedYear, setSelectedYear] = useState<number | undefined>()
  const [selectedTag, setSelectedTag] = useState('All')
  const { data: statsData } = useEntryStats()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useEntries(selectedYear)

  const allEntries = data?.pages.flat() ?? []

  const filtered =
    selectedTag === 'All'
      ? allEntries
      : allEntries.filter(e =>
          e.tags.some(t =>
            t.toLowerCase().includes(
              selectedTag.split(' ')[1]?.toLowerCase() ?? ''
            )
          )
        )

  const years = statsData?.years ?? []

  function groupByDate(entries: Entry[]) {
    return entries.reduce((groups, entry) => {
      const d = parseISO(entry.recorded_at)
      const key = isToday(d)
        ? 'Today'
        : isYesterday(d)
        ? 'Yesterday'
        : format(d, 'MMMM d, yyyy')
      if (!groups[key]) groups[key] = []
      groups[key].push(entry)
      return groups
    }, {} as Record<string, Entry[]>)
  }

  const grouped = groupByDate(filtered)

  return (
    <div className="flex flex-col min-h-full">
      {/* Top bar */}
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-3 safe-top flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/icon.png" alt="" className="w-8 h-8 rounded-lg" />
          <div>
            <h1 className="font-serif text-xl font-bold text-white leading-tight">
              My Timeline
            </h1>
            <p className="font-mono text-[9px] text-white/30 tracking-widest uppercase">
              History in the Making
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/timeline/chronological"
            className="w-9 h-9 rounded-xl bg-white/5 border border-white/[0.07] flex items-center justify-center"
          >
            <ChevronRight size={18} className="text-white/50" />
          </Link>
          <Link
            to="/notifications"
            className="w-9 h-9 rounded-xl bg-white/5 border border-white/[0.07] flex items-center justify-center relative"
          >
            <Bell size={18} className="text-white/50" />
            <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-rose rounded-full border-2 border-bg" />
          </Link>
        </div>
      </div>

      {/* On This Day banner */}
      <OnThisDayBanner />

      {/* Year scroller */}
      {years.length > 0 && (
        <YearScroller
          years={years}
          selected={selectedYear}
          onSelect={y => setSelectedYear(selectedYear === y ? undefined : y)}
        />
      )}

      {/* Tag filters */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
        {TAGS.map(tag => (
          <button
            key={tag}
            onClick={() => setSelectedTag(tag)}
            className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all
              ${selectedTag === tag
                ? 'border-blue text-blue-light bg-blue/10'
                : 'border-white/[0.07] text-white/40 bg-white/5'}`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Entry feed */}
      <div className="flex flex-col px-4 gap-1 pb-8">
        {Object.keys(grouped).length === 0 ? (
          /* Empty state — fully centered */
          <div className="flex flex-col items-center justify-center text-center py-24 gap-5 px-6">
            <div className="text-6xl">📅</div>
            <div>
              <p className="font-serif text-2xl font-bold text-white/60 mb-2">
                Your timeline begins
              </p>
              <p className="font-serif text-lg italic text-white/30">
                with your first memory.
              </p>
            </div>
            <Link
              to="/timeline/create"
              className="mt-2 px-8 py-3.5 rounded-2xl font-sans font-semibold text-white text-sm tracking-wide"
              style={{ background: 'linear-gradient(135deg, #4f6ef7, #8b5cf6)', boxShadow: '0 4px 20px rgba(79,110,247,0.3)' }}
            >
              Add Your First Memory
            </Link>
          </div>
        ) : (
          Object.entries(grouped).map(([date, entries]) => (
            <div key={date}>
              <div className="flex items-center gap-3 py-3">
                <span className="font-mono text-[10px] text-white/30 tracking-widest uppercase">
                  {date}
                </span>
                <div className="flex-1 h-px bg-white/[0.05]" />
              </div>
              {entries.map(entry => (
                <EntryCard key={entry.id} entry={entry} />
              ))}
            </div>
          ))
        )}

        {hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="btn-secondary mt-4 max-w-xs mx-auto"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  )
}
